import { ChevronRightIcon } from "lucide-react";
import { Link } from "react-router-dom";

import { cn } from "@/utils/shadcn-ui";

import {
  IconOnlyChevron,
  matchesRoute,
  type MenuItem,
  NavFlyout,
  useNavFlyout,
} from "./menu-list";

/**
 * Level 1: the icon rail. Always mounted and never scrolled away, so the global
 * destinations stay reachable from inside a patient — the whole point of the
 * two-level model. Labels fade out when the second-level panel covers them, and
 * again when the rail collapses; the icons never move, in either case.
 */
export function RailMenu({
  items,
  activeSection,
  labelsHidden,
  collapsed,
}: {
  items: MenuItem[];
  activeSection?: MenuItem;
  /** Labels are not readable: the level-2 panel covers them, or the rail
   * narrowed past them. */
  labelsHidden: boolean;
  collapsed: boolean;
}) {
  const flyout = useNavFlyout();
  const visible = items.filter((item) => item.show !== false);

  return (
    <ul className="flex w-full min-w-0 flex-col gap-[3px]">
      {visible.map((item) => {
        const active = item === activeSection;
        const hasLevels = (item.children ?? []).some((c) => c.show !== false);
        const Icon = item.icon;
        // Only shown where a label is: it reads as "this section has pages",
        // and once a panel is open it would sit under the panel's edge — or,
        // with the panel collapsed, out past the sidebar's own edge entirely.
        const showChevron = hasLevels && !collapsed && !labelsHidden;

        return (
          <li
            key={item.to}
            // Named group so the row's hover follows the pointer onto the
            // chevron, which is a sibling of the link rather than inside it —
            // a plain `hover:` on the link drops the moment you reach the
            // chevron. Named, because the sidebar root is itself a `group`.
            className="group/rail-row relative"
            // Focus events bubble in React, so listening here covers the link
            // without wiring the handlers onto it — a row reached by keyboard
            // needs its label as much as a hovered one does.
            {...flyout.triggerProps(`rail:${item.to}`, labelsHidden)}
          >
            <Link
              to={item.to}
              aria-current={active ? "page" : undefined}
              className={cn(
                // 140ms plain `ease` per the handoff — deliberately not the
                // shared 280ms curve, which is for motion (width, slide, fade);
                // hover feedback is quicker and uses a different easing.
                "relative flex h-10 items-center gap-3 rounded-[9px] px-2.5 transition-colors duration-140 ease-[ease] hover:no-underline",
                // Hover is for rows you can still go to: on the current row it
                // would trade the selected fill for the neutral hover surface,
                // which in dark reads as the state dropping out under the
                // pointer.
                !active && "group-hover/rail-row:bg-sidebar-accent",
                active && "bg-nav-active",
              )}
            >
              {/* Fixed 20px box: keeps the glyph pixel-aligned between the
                  expanded and collapsed states so it never shifts. */}
              <span className="flex w-5 flex-none items-center justify-center">
                {Icon && (
                  <Icon
                    className={cn(
                      // 160ms: the icon's colour change trails the row's 140ms
                      // background very slightly, so the glyph reads as the
                      // thing that "lights up" rather than both landing
                      // together.
                      "size-[19px] transition-colors duration-160 ease-[ease]",
                      active ? "text-nav-active-foreground" : "text-nav-icon",
                    )}
                    strokeWidth={1.7}
                  />
                )}
              </span>

              <span
                className={cn(
                  // `transform` on both the class and the transition list, for
                  // the reason described on the level-2 panel in app-sidebar.tsx.
                  "min-w-0 flex-1 truncate text-[14px] transition-[opacity,transform] duration-280 ease-[cubic-bezier(.4,0,.2,1)] motion-reduce:transition-none",
                  // One weight step apart, not two: the ink, the wash and the
                  // marker already rank the current row, so 500-over-400 is
                  // enough and keeps the nav from reading as bold text.
                  active
                    ? "text-nav-active-foreground font-medium"
                    : "text-sidebar-foreground font-normal",
                  labelsHidden && "opacity-0 [transform:translateX(-6px)]",
                )}
              >
                {item.label}
              </span>
            </Link>

            {showChevron && (
              // The panel can only show this section's pages by going there
              // first, so a separate action would be a click that does nothing.
              // Inert, letting the press fall through to the link underneath —
              // the arrow behaves as part of the row.
              <span
                aria-hidden
                className={cn(CHEVRON_BOX, "pointer-events-none")}
              >
                <ChevronRightIcon
                  className={cn(
                    "size-3.5",
                    active ? "text-nav-active-foreground" : "text-nav-icon",
                  )}
                  strokeWidth={2.2}
                />
              </span>
            )}

            {/* The icon-only rail's version, the same as the level-2 strip's so
                both columns read alike. */}
            {hasLevels && labelsHidden && (
              <IconOnlyChevron active={active} className="size-3.5" />
            )}

            {/* Gated on the same condition that hides the label, not on
                `collapsed` alone: the panel hides it too, and that case used to
                fall through to no label and no tooltip at all. The section
                alone, without its pages: those are the level-2 panel's. */}
            {labelsHidden && (
              <NavFlyout
                id={`rail:${item.to}`}
                entries={[{ item, current: active }]}
                // The row starts 8px in (the list's padding), so 48px reaches
                // the rail's edge at 56 — the rail owns the sidebar's leading
                // 56px whichever of the two reasons hid the label.
                width="w-12"
                rowHeight={40}
              />
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** The arrow's box: at the row's right end, vertically centred. */
const CHEVRON_BOX =
  "absolute top-1/2 right-2 flex size-[22px] -translate-y-1/2 items-center justify-center rounded-md";

/** The top-level section the current URL belongs to. */
export function findActiveSection(
  items: MenuItem[],
  pathname: string,
): MenuItem | undefined {
  return items.find(
    (item) => item.show !== false && matchesRoute(item.to, pathname),
  );
}
